import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { useSearchParams } from "react-router-dom";
import { Stack } from "@/components/layout";
import { Text } from "@/components/typography";
import { Alert, Button, LoadingState, Tabs, type TabItem } from "@/components/ui";
import { apiErrorMessage } from "@/api/client";
import { cn } from "@/lib/cn";
import styles from "./hub.module.css";

// ---------------------------------------------------------------------------
// Embedded queues
// ---------------------------------------------------------------------------

export interface HubEmbedded {
  crumbs: { label: string; to?: string }[];
}

const EmbeddedContext = createContext<HubEmbedded | null>(null);

/** Wraps a hub's tab content so an embedded queue page knows it is inside one. */
export function HubEmbeddedProvider({
  crumbs,
  children,
}: {
  crumbs: HubEmbedded["crumbs"];
  children: ReactNode;
}) {
  return <EmbeddedContext.Provider value={{ crumbs }}>{children}</EmbeddedContext.Provider>;
}

/** Non-null when the calling queue page is rendered inside a hub tab. */
export function useHubEmbedded(): HubEmbedded | null {
  return useContext(EmbeddedContext);
}

// ---------------------------------------------------------------------------
// Tabs whose selection lives in the URL, so back-navigation from a detail page
// lands on the same tab.
// ---------------------------------------------------------------------------

export interface HubTabItem {
  id: string;
  label: ReactNode;
  content: ReactNode;
  /** Small count shown next to the label (open work). Hidden at 0 or undefined. */
  badge?: number | undefined;
  badgeTone?: "warning" | "danger" | "neutral";
}

function tabLabel(item: HubTabItem): ReactNode {
  if (!item.badge) return item.label;
  return (
    <span className={styles.tabLabel}>
      {item.label}
      <span
        aria-label={`${item.badge} open`}
        style={{
          fontSize: "var(--type-caption-size)",
          padding: "0 6px",
          borderRadius: "var(--radius-full)",
          background:
            item.badgeTone === "danger"
              ? "var(--color-danger)"
              : item.badgeTone === "neutral"
                ? "var(--color-gray-200)"
                : "var(--color-warning)",
          color: item.badgeTone === "neutral" ? "var(--color-text-primary)" : "var(--color-white)",
        }}
      >
        {item.badge > 99 ? "99+" : item.badge}
      </span>
    </span>
  );
}

export function HubTabs({
  items,
  param = "tab",
  variant = "main",
}: {
  items: HubTabItem[];
  /** URL search parameter holding the selection. Nested tabs use "sub". */
  param?: string;
  variant?: "main" | "sub";
}) {
  const [params, setParams] = useSearchParams();
  const requested = params.get(param);
  const value = items.some((i) => i.id === requested) ? (requested as string) : items[0]?.id;

  const tabItems: TabItem[] = items.map((i) => ({
    id: i.id,
    label: tabLabel(i),
    content: i.content,
  }));

  return (
    <div className={variant === "sub" ? styles.subTabs : styles.tabsWrap}>
      <Tabs
        items={tabItems}
        {...(value ? { value } : {})}
        onValueChange={(id) => {
          const next = new URLSearchParams(params);
          next.set(param, id);
          if (param === "tab") next.delete("sub");
          setParams(next, { replace: false });
        }}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Data loading
// ---------------------------------------------------------------------------

export function useHubLoad<T>(loader: () => Promise<T>) {
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const seq = useRef(0);

  const reload = useCallback(async () => {
    const mine = ++seq.current;
    setLoading(true);
    setError(null);
    try {
      const result = await loader();
      if (mine === seq.current) setData(result);
    } catch (err) {
      if (mine === seq.current) setError(apiErrorMessage(err));
    } finally {
      if (mine === seq.current) setLoading(false);
    }
  }, [loader]);

  useEffect(() => {
    void reload();
    return () => {
      seq.current++;
    };
  }, [reload]);

  return { data, error, loading, reload };
}

/** Loading / error-with-retry wrapper so every overview handles both the same way. */
export function LoadBoundary({
  loading,
  error,
  onRetry,
  hasData,
  what,
  children,
}: {
  loading: boolean;
  error: string | null;
  onRetry: () => void;
  hasData: boolean;
  what: string;
  children: ReactNode;
}) {
  if (error && !hasData) {
    return (
      <Alert
        tone="danger"
        title={`Could not load ${what}`}
      >
        <Stack gap={8}>
          <span>{error}</span>
          <div>
            <Button variant="secondary" size="sm" onClick={onRetry}>
              Retry
            </Button>
          </div>
        </Stack>
      </Alert>
    );
  }
  if (loading && !hasData) return <LoadingState message={`Loading ${what}…`} />;
  return (
    <Stack gap={16}>
      {error ? (
        <Alert tone="warning" title={`Showing the last loaded ${what}`}>
          Refreshing failed: {error}
        </Alert>
      ) : null}
      {children}
    </Stack>
  );
}

// ---------------------------------------------------------------------------
// Building blocks
// ---------------------------------------------------------------------------

export function KpiGrid({ children }: { children: ReactNode }) {
  return <div className={styles.kpiGrid}>{children}</div>;
}

export function KpiCard({
  label,
  value,
  hint,
  tone,
  onClick,
}: {
  label: string;
  value: ReactNode;
  hint?: ReactNode;
  tone?: "danger" | "warning";
  onClick?: () => void;
}) {
  const body = (
    <>
      <span className={styles.kpiLabel}>{label}</span>
      <span
        className={cn(
          styles.kpiValue,
          tone === "danger" && styles.kpiValueDanger,
          tone === "warning" && styles.kpiValueWarning,
        )}
      >
        {value}
      </span>
      {hint ? <span className={styles.kpiHint}>{hint}</span> : null}
    </>
  );
  if (onClick) {
    return (
      <button type="button" className={styles.kpi} onClick={onClick}>
        {body}
      </button>
    );
  }
  return <div className={styles.kpi}>{body}</div>;
}

export function Panel({
  title,
  description,
  actions,
  children,
}: {
  title: string;
  description?: ReactNode;
  actions?: ReactNode;
  children: ReactNode;
}) {
  return (
    <section className={styles.panel}>
      <div className={styles.panelHeader}>
        <Stack gap={4}>
          <Text role="title" as="h3">
            {title}
          </Text>
          {description ? (
            <Text role="small" color="secondary" as="p">
              {description}
            </Text>
          ) : null}
        </Stack>
        {actions ? <div>{actions}</div> : null}
      </div>
      {children}
    </section>
  );
}

export function TwoCol({ children }: { children: ReactNode }) {
  return <div className={styles.twoCol}>{children}</div>;
}

export interface AttentionEntry {
  key: string;
  severity: "critical" | "warning" | "info";
  title: string;
  detail?: string;
  actionLabel?: string;
  onAction?: () => void;
}

/** What needs a human right now. An empty list is shown as an explicit all-clear. */
export function AttentionList({
  entries,
  clearMessage,
}: {
  entries: AttentionEntry[];
  clearMessage: string;
}) {
  if (entries.length === 0) {
    return (
      <Text role="body" color="secondary" as="p">
        {clearMessage}
      </Text>
    );
  }
  return (
    <ul className={styles.attentionList}>
      {entries.map((e) => (
        <li
          key={e.key}
          className={cn(
            styles.attentionItem,
            e.severity === "critical" && styles.attentionCritical,
            e.severity === "warning" && styles.attentionWarning,
            e.severity === "info" && styles.attentionInfo,
          )}
        >
          <Stack gap={4}>
            <Text role="bodyStrong" as="span">
              {e.title}
            </Text>
            {e.detail ? (
              <Text role="small" color="secondary" as="span">
                {e.detail}
              </Text>
            ) : null}
          </Stack>
          {e.onAction ? (
            <button type="button" className={styles.attentionAction} onClick={e.onAction}>
              {e.actionLabel ?? "Open"}
            </button>
          ) : null}
        </li>
      ))}
    </ul>
  );
}

export interface BarRow {
  key: string;
  label: string;
  value: number;
  display: string;
  tone?: "warning" | "danger" | "success" | undefined;
}

/** A horizontal bar per row, scaled to the largest value. */
export function BarList({ rows, emptyMessage }: { rows: BarRow[]; emptyMessage: string }) {
  if (rows.length === 0) {
    return (
      <Text role="body" color="secondary" as="p">
        {emptyMessage}
      </Text>
    );
  }
  const max = Math.max(...rows.map((r) => r.value), 1);
  return (
    <Stack gap={8}>
      {rows.map((r) => (
        <div key={r.key} className={styles.barRow}>
          <Text role="small" as="span">
            {r.label}
          </Text>
          <div className={styles.barTrack} aria-hidden>
            <div
              className={cn(
                styles.barFill,
                r.tone === "warning" && styles.barFillWarning,
                r.tone === "danger" && styles.barFillDanger,
                r.tone === "success" && styles.barFillSuccess,
              )}
              style={{ width: `${Math.max(2, (r.value / max) * 100)}%` }}
            />
          </div>
          <Text role="small" as="span" className={styles.mono}>
            {r.display}
          </Text>
        </div>
      ))}
    </Stack>
  );
}

/** One column per day. Every column has a title with its exact figure. */
export function DayColumns({
  days,
  emptyMessage,
}: {
  days: { day: string; value: number; title: string }[];
  emptyMessage: string;
}) {
  if (days.length === 0) {
    return (
      <Text role="body" color="secondary" as="p">
        {emptyMessage}
      </Text>
    );
  }
  const max = Math.max(...days.map((d) => d.value), 1);
  const first = days[0]!;
  const last = days[days.length - 1]!;
  return (
    <div>
      <div className={styles.columns} role="img" aria-label="Daily totals">
        {days.map((d) => (
          <div
            key={d.day}
            className={styles.column}
            title={d.title}
            style={{ height: `${Math.max(3, (d.value / max) * 100)}%` }}
          />
        ))}
      </div>
      <div className={styles.columnLabels}>
        <span>{first.day}</span>
        <span>{last.day}</span>
      </div>
    </div>
  );
}

/**
 * An honest gap: a capability that has no table or no Play counterpart yet.
 * Says what is missing, what a real build needs, and the recommendation —
 * never a placeholder number.
 */
export function NotConnected({
  title,
  missing,
  needs,
  recommendation,
}: {
  title: string;
  missing: string;
  needs?: string[];
  recommendation?: string;
}) {
  return (
    <div className={styles.notConnected}>
      <Text role="title" as="h3">
        {title}
      </Text>
      <Text role="body" color="secondary" as="p">
        {missing}
      </Text>
      {needs && needs.length > 0 ? (
        <ul>
          {needs.map((n) => (
            <li key={n}>
              <Text role="small" color="secondary" as="span">
                {n}
              </Text>
            </li>
          ))}
        </ul>
      ) : null}
      {recommendation ? (
        <Text role="small" as="p">
          <strong>Recommendation:</strong> {recommendation}
        </Text>
      ) : null}
    </div>
  );
}

export { styles as hubStyles };

/** Jump to another tab (and optionally a sub-tab) of the current hub. */
export function useHubNavigate() {
  const [params, setParams] = useSearchParams();
  return useCallback(
    (tab: string, sub?: string) => {
      const next = new URLSearchParams(params);
      next.set("tab", tab);
      if (sub) next.set("sub", sub);
      else next.delete("sub");
      setParams(next, { replace: false });
      window.scrollTo?.({ top: 0 });
    },
    [params, setParams],
  );
}
